import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Image } from '@chakra-ui/react';
import type { BigNumber } from '@ethersproject/bignumber';
import Balance from '@ui/components/Balance';
import { InfoWithLoading } from '@ui/components/Info';
import type { InfoTooltipProps } from '@ui/components/InfoTooltip';
import { InfoTooltip } from '@ui/components/InfoTooltip';
import { TokenIcon } from '@ui/components/TokenIcon';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useTranslations } from '@ui/i18n';
import { formatTokenAmount } from '@ui/utils/format';
import { useAllocationOfPendingRewards } from '@ui/utils/garden';
import type { ReactNode } from 'react';

import DistributionExplainer from './DistributionExplainer';

export type GardenVnoAllocationInfoProps = {
  vnoAmount: BigNumber | undefined;
  vnoAmountTitle: string;
  allocationTitle: ReactNode;
  intoWalletTitle: ReactNode;
  into12MonthTitle: ReactNode;
};
export const GardenVnoAllocationInfo = ({
  vnoAmount,
  vnoAmountTitle,
  allocationTitle,
  intoWalletTitle,
  into12MonthTitle,
}: GardenVnoAllocationInfoProps) => {
  const { data: vnoAmountUsd } = useVnoUsdValue(vnoAmount);

  return (
    <>
      <Balance
        title={vnoAmountTitle}
        amount={vnoAmount}
        icon={<Image alt="" src="/tokens/vno.svg" />}
        usdAmount={vnoAmountUsd}
        usdDisplayDecimals={2}
        showUsdAmount
      />
      <Flex textStyle="bodyBold" mt="24px" textAlign="left">
        {allocationTitle}
        <GardenVnoAllocationTooltip
          hidden={typeof allocationTitle !== 'string'}
        />
      </Flex>
      <GardenVnoAllocationBody
        mt="16px"
        vnoAmount={vnoAmount}
        intoWalletTitle={intoWalletTitle}
        into12MonthTitle={into12MonthTitle}
      />
    </>
  );
};

export type GardenVnoAllocationTooltipProps = Partial<InfoTooltipProps>;
export const GardenVnoAllocationTooltip = (
  props: GardenVnoAllocationTooltipProps,
) => {
  const t = useTranslations();
  return (
    <InfoTooltip
      title={t('{coin} distribution', {
        coin: 'VNO',
      })}
      label={<DistributionExplainer />}
      {...props}
    />
  );
};

export type GardenVnoAllocationBodyProps = BoxProps & {
  vnoAmount: BigNumber | undefined;
  intoWalletTitle: ReactNode;
  into12MonthTitle: ReactNode;
};
export const GardenVnoAllocationBody = ({
  vnoAmount,
  intoWalletTitle,
  into12MonthTitle,
  ...props
}: GardenVnoAllocationBodyProps) => {
  const { into12MonthVault, intoWallet } =
    useAllocationOfPendingRewards(vnoAmount);

  const { data: into12MonthVaultUsd } = useVnoUsdValue(into12MonthVault);
  const { data: intoWalletUsd } = useVnoUsdValue(intoWallet);

  return (
    <Box {...props}>
      <InfoWithLoading
        isLoading={!intoWallet}
        title={intoWalletTitle}
        value={formatTokenAmount(intoWallet)}
        icon={(props) => <TokenIcon token="vno" {...props} />}
        convertedValue={`${formatTokenAmount(intoWalletUsd, 2)} USD`}
      />
      <InfoWithLoading
        isLoading={!into12MonthVault}
        title={into12MonthTitle}
        value={formatTokenAmount(into12MonthVault)}
        icon={(props) => <TokenIcon token="vno" {...props} />}
        convertedValue={`${formatTokenAmount(into12MonthVaultUsd, 2)} USD`}
        mt="12px"
      />
    </Box>
  );
};
